<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-modal jps-db-selector"
      :no-header="true"
      dialog-width="96vw"
      dialog-height="80vh"
    >
      <div v-if="ss" class="jps-modal-content">
        <SearchBox :ss="ss" />
        <SearchCount :ss="ss" />
        <div v-if="ss.searched" class="db-panel-list">
          <UiDataPanel
            class="db-panel"
            v-for="db in ss.state.result.list"
            :class="{ 'jps-selected': selectedValue.includes(db.id) }"
            :key="db.id"
            :nolink=true
            type="database"
            :data="db"
            :tablink=true
            @click="selectDb(db)"
          />
          <SearchInfiniteLoading :ss="ss" />
        </div>
      </div>
      <template #footer-extra v-if="_multiple">
        <div class="modal-footer-selected">
          <UiDbSpan
            class="jps-tag jps-dark jps-tag-ricon"
            v-for="db in selectedValue"
            :key="db"
            :id="db"
            tag="span"
          >
            <template #default="{ db }">
              {{ ls(db.name) }}
              <BaseIcon
                class="jps-icon-button is-size-6"
                name="mdi/close-circle"
                @click="removeFromArray(selectedValue, db.id)"
              />
            </template>
          </UiDbSpan>
        </div>
      </template>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

const { ls } = useI18n()
const { searchDatabase } = useDatabase()
const dataStore = useDataStore()

interface Props {
  portal?: boolean,
  multiple?: boolean,
  selected?: string[]
}
const props = withDefaults(defineProps<Props>(),{
  portal: false
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _portal: Ref<boolean> = ref(false)
const _multiple: Ref<boolean> = ref(false)
const _selected: Ref<string[]> = ref([])
const ss: Ref<SearchStore<Database>> = ref(null)
const selectedValue: Ref<string[]> = ref([])
const openAwait = async (options?: Props) => {
  setOptionsValue(_portal, 'portal', props, options, false)
  setOptionsValue(_multiple, 'multiple', props, options, false)
  setOptionsValue(_selected, 'selected', props, options, [])

  if (_selected.value) selectedValue.value = clone(_selected.value)
  ss.value = useSearch(
    { keywordFields: ['keywords'] },
    searchDatabase,
    {
      configFreeze: false,
      configSyncRoute: false,
      configCacheSubKey: 'dbselect',
      queryRewriter: (q: any) => {
        if (_portal.value) q.permission = 'P'
        else q.permission = 'M'
        return q
      }
    }
  )
  ss.value.execute()
  nextTick(() => {
    ss.value.requestKeywordFocus()
  })
  return await modal.value.openAwait<string[]>()
}
defineExpose({ openAwait })

const selectDb = (db: Database) => {
  if (_multiple.value) {
    if (!selectedValue.value.includes(db.id)) {
      dataStore.put('database', db.id, db)
      selectedValue.value.push(db.id)
    } else {
      removeFromArray(selectedValue.value, db.id)
    }
  } else {
    modal.value.resolve([db.id])
  }
}

const confirm = () => {
  modal.value.resolve(selectedValue.value)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-db-selector {

  .db-panel {
    cursor: pointer;

    &:not(:first-child) {
      margin-top: 0.5em;
    }

    &.jps-selected {
      background: $jps-key-blue;

      h3,
      p {
        color: $white;
      }
    }
  }

  .media {
    @include box-shadow;

    &:hover {
      border: 1px solid $jps-gray-300;
      background: $jps-gray-100;
    }

    background: $white;
    padding: 0.5rem;
    border: 1px solid $white;

    h3 {
      margin-top: 0.5rem;
    }

    p {
      line-height: 125%;
    }
  }

  .modal-footer-selected {
    padding: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;

    .jps-tag {
      margin: 0.125rem 0;
    }

    max-height: 116px;
    overflow-y: auto;
  }
}
</style>
